/**
 * Shared utilities for input components to handle help hover functionality
 */

/**
 * Update help hover for an input component
 *
 * @param host - The host element (input component)
 * @param helpHoverContainer - The container element where help-hover should be placed
 * @param savedHovercard - Optional saved hovercard element (for light DOM after innerHTML wipe)
 */
export function updateInputHelpHover(
  host: HTMLElement,
  helpHoverContainer: HTMLElement | null,
  savedHovercard?: Element | null
): void {
  if (!helpHoverContainer) return;

  const helpTooltipDefaultText = host.getAttribute('help-tooltip-default-text');
  const helpTooltipDictKey = host.getAttribute('help-tooltip-dict-key');
  const helpPlacement = host.getAttribute('help-placement') ?? 'right';

  // Use saved hovercard if provided (for light DOM after innerHTML wipe),
  // otherwise query for it (for shadow DOM or attribute changes)
  const hovercard = savedHovercard || host.querySelector('as-hovercard');

  // Only update if we have tooltip attributes or a hovercard
  if (helpTooltipDefaultText || helpTooltipDictKey || hovercard) {
    // Clear existing help-hover
    helpHoverContainer.innerHTML = '';

    // Create new help-hover
    const helpHover = document.createElement('as-help-hover');

    if (helpTooltipDefaultText) {
      helpHover.setAttribute('help-tooltip-default-text', helpTooltipDefaultText);
    }

    if (helpTooltipDictKey) {
      helpHover.setAttribute('help-tooltip-dict-key', helpTooltipDictKey);
    }

    helpHover.setAttribute('placement', helpPlacement);

    // Move hovercard into help-hover if it exists
    if (hovercard) {
      helpHover.appendChild(hovercard);
    }

    helpHoverContainer.appendChild(helpHover);
  } else {
    // Clear help hover if no content
    helpHoverContainer.innerHTML = '';
  }
}

/**
 * Update help hover for a shadow DOM component (like as-dropdown)
 *
 * @param host - The host element (input component with shadow DOM)
 * @param helpHoverContainer - The container element in shadow DOM where help-hover should be placed
 */
export function updateShadowDOMHelpHover(
  host: HTMLElement,
  helpHoverContainer: HTMLElement | null
): void {
  if (!helpHoverContainer) return;

  const helpTooltipDefaultText = host.getAttribute('help-tooltip-default-text');
  const helpTooltipDictKey = host.getAttribute('help-tooltip-dict-key');
  const helpPlacement = host.getAttribute('help-placement') ?? 'right';

  // Check if there's a child as-hovercard in the light DOM (host element)
  // Note: 'host' refers to the host element, which has the light DOM children
  const hovercard = Array.from(host.children).find(
    child => child.tagName.toLowerCase() === 'as-hovercard'
  ) as HTMLElement | undefined;

  // Only update if we have tooltip attributes or a hovercard
  if (helpTooltipDefaultText || helpTooltipDictKey || hovercard) {
    // Clear existing help-hover to ensure clean state (consistent with light DOM behavior)
    helpHoverContainer.innerHTML = '';

    // Create new help-hover
    const helpHover = document.createElement('as-help-hover');

    if (helpTooltipDefaultText) {
      helpHover.setAttribute('help-tooltip-default-text', helpTooltipDefaultText);
    }

    if (helpTooltipDictKey) {
      helpHover.setAttribute('help-tooltip-dict-key', helpTooltipDictKey);
    }

    helpHover.setAttribute('placement', helpPlacement);

    // Move hovercard into help-hover if it exists
    if (hovercard) {
      helpHover.appendChild(hovercard);
    }

    helpHoverContainer.appendChild(helpHover);
  } else {
    // Clear help hover if no content
    helpHoverContainer.innerHTML = '';
  }
}
